/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { applyResellerTheme } from "utils/reseller-theme";

interface AppConfigContextType {
  appName: string;
  // A reseller may bring their own reCAPTCHA key — that is what sidesteps Google's cap of 250
  // domains per key on the shared platform key. /branding returns it per host, so it arrives with
  // the rest of the branding and is undefined until then; consumers fall back to the build-time key.
  recaptchaSiteKey?: string;
}

interface AppConfigProviderProps {
  children: ReactNode;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

const applySettingsToDOM = (
  settings: any,
  setAppName: (n: string) => void,
  setRecaptchaSiteKey: (k?: string) => void
) => {
  setRecaptchaSiteKey(settings.recaptcha_site_key || undefined);

  const audioUrl = process.env.REACT_APP_URL_FOR_AUDIO || "";

  // Meta description
  const metaDescription = document.querySelector("meta[name='description']");
  if (metaDescription) {
    metaDescription.setAttribute("content", settings.website_description || "");
  } else {
    const newMeta = document.createElement("meta");
    newMeta.name = "description";
    newMeta.content = settings.website_description || "";
    document.head.appendChild(newMeta);
  }

  // Page title / app name
  if (settings.website_name) setAppName(settings.website_name);

  // Favicon
  const faviconHref = settings.favicon_logo
    ? audioUrl + settings.favicon_logo
    : "";
  if (faviconHref) {
    const linkFavicon = document.querySelector("link[rel='icon']");
    if (linkFavicon) {
      (linkFavicon as HTMLLinkElement).href = faviconHref;
    } else {
      const newFavicon = document.createElement("link");
      newFavicon.rel = "icon";
      newFavicon.href = faviconHref;
      document.head.appendChild(newFavicon);
    }
  }

  // The reseller's accent, as the --qc-iris-* ramp this design system actually resolves through.
  // Gated on is_white_label inside applyResellerTheme, so a platform host clears it instead.
  applyResellerTheme(settings);
};

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  const [appName, setAppName] = useState<string>("");
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchConfig = async () => {
      const baseUrl = process.env.REACT_APP_BASE_URL;

      // Step 1: Try per-domain /branding endpoint first
      try {
        const brandRes = await fetch(`${baseUrl}/branding?host=${window.location.hostname}`);
        const brandData = await brandRes.json();
        if (brandData?.success && brandData?.data?.length > 0) {
          const settings = brandData.data[0];
          // Normalize white-label branding keys to match existing appSetting shape.
          // The /branding response keys intentionally match /getSettings keys already.
          const merged = {
            ...JSON.parse(localStorage.getItem("appSetting") || "{}"),
            ...settings,
          };
          localStorage.setItem("appSetting", JSON.stringify(merged));
          applySettingsToDOM(settings, setAppName, setRecaptchaSiteKey);
          return; // branding loaded — skip fallback
        }
      } catch {
        // branding endpoint unavailable — fall through to /getSettings
      }

      // Step 2: Fallback — existing /getSettings call (platform domain unchanged)
      try {
        const response = await fetch(`${baseUrl}/getSettings`);
        const data = await response.json();
        if (data.success && data?.data?.length > 0) {
          localStorage.setItem("appSetting", JSON.stringify(data.data[0]));
          applySettingsToDOM(data.data[0], setAppName, setRecaptchaSiteKey);
        } else {
          localStorage.setItem("appSetting", JSON.stringify({}));
        }
      } catch {
        localStorage.setItem("appSetting", JSON.stringify({}));
      }
    };

    fetchConfig();
  }, []);

  return (
    <AppConfigContext.Provider value={{ appName, recaptchaSiteKey }}>
      {children}
    </AppConfigContext.Provider>
  );
};
